import { useEffect } from "react";
import { 
  Box, 
  CircularProgress, 
  Container, 
  Paper,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../../api/axios";
import { useSnackbar } from "notistack";
import useAuth from "../../hooks/useAuth";
import {
  clearPersistedPostAuthReturn,
  getPersistedPostAuthReturn,
} from "../../utils/postAuthNavigation";

const InternalToken = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setAuth, setAuthWithTenant, setCurrentTenant } = useAuth();
  
  const code = searchParams.get("code");
  const consentId = searchParams.get("consent_id") || searchParams.get("consentId");
  const tenantId = searchParams.get("tenant_id") || searchParams.get("tenantId");

  const parseTenantIdFromIdToken = (idToken) => {
    if (!idToken || typeof idToken !== "string") return null;
    const decodeBase64Url = (value) => {
      const padded = value.padEnd(
        value.length + ((4 - (value.length % 4)) % 4),
        "="
      );
      return atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    };

    const extractTenantIdFromDecoded = (decoded) => {
      // Supports raw JSON and payloads that include a trailing signature blob.
      const jsonStart = decoded.indexOf("{");
      const jsonEnd = decoded.lastIndexOf("}");
      if (jsonStart === -1 || jsonEnd === -1 || jsonEnd < jsonStart) return null;
      const parsed = JSON.parse(decoded.slice(jsonStart, jsonEnd + 1));
      return parsed?.tenant_id || parsed?.tenantId || null;
    };

    try {
      const parts = idToken.split(".");
      if (parts.length < 2) return null;

      const candidatePayloadParts = [];
      if (parts[1] && parts[1] !== "public" && parts[1] !== "local") {
        candidatePayloadParts.push(parts[1]); // JWT style
      }
      if (parts[2]) {
        candidatePayloadParts.push(parts[2]); // PASETO style
      }

      for (const payloadPart of candidatePayloadParts) {
        try {
          const decoded = decodeBase64Url(payloadPart);
          const tenant = extractTenantIdFromDecoded(decoded);
          if (tenant) return tenant;
        } catch {
          // Try next candidate.
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const exchangeToken = async () => {
      if (!code) {
        enqueueSnackbar("Authorization code is missing", { variant: "error" });
        navigate("/login");
        return;
      }

      try {
        const response = await axios.post(
          "/internalToken",
          { code },
          { withCredentials: true }
        );

        const tokenData = response?.data?.data || response?.data;
        
        if (tokenData?.access_token) {
           const resolvedTenantId =
             tenantId ||
             tokenData?.tenant_id ||
             parseTenantIdFromIdToken(tokenData?.id_token);

           // Success! Update auth state
           setAuth(tokenData);
           if (resolvedTenantId) {
             setCurrentTenant({ id: resolvedTenantId });
             setAuthWithTenant(tokenData, resolvedTenantId);
           }
           
           if (consentId) {
             navigate(`/consent?consentId=${consentId}`, { replace: true });
           } else {
             const profileReturn = getPersistedPostAuthReturn();
             if (profileReturn) {
               clearPersistedPostAuthReturn();
               navigate(profileReturn, { replace: true });
             } else {
               navigate("/", { replace: true });
             }
           }
        } else {
           enqueueSnackbar("Failed to exchange token", { variant: "error" });
           navigate("/login");
        }
      } catch (error) {
        console.error("Token exchange error:", error);
        enqueueSnackbar(error?.response?.data?.error?.message || "Error exchanging token", { variant: "error" });
        navigate("/login");
      }
    };

    exchangeToken();
  }, [
    code,
    consentId,
    navigate,
    enqueueSnackbar,
    setAuth,
    setAuthWithTenant,
    setCurrentTenant,
    tenantId,
  ]);

  return (
    <Container maxWidth="xs" sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh' 
    }}>
      <Paper elevation={0} sx={{
        p: 4,
        borderRadius: 4,
        textAlign: 'center',
        background: 'transparent',
        width: '100%'
      }}>
        <CircularProgress size={40} sx={{ mb: 2, color: 'primary.main' }} />
      </Paper>
    </Container>
  );
};

export default InternalToken;
